import { useState, useContext, useRef, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Alert from "../Alert";
import PermissionGate from "../layout/PermissionGate";
import { alertContext } from "../contexts/AlertProvider";
import normalize from "../utils/NormalizeSelect";

const PAYMENT_OPTIONS = [
    { value: "Cash on Delivery", label: "Cash on Delivery" },
    { value: "Paid", label: "Paid" },
];

const FIELD_DEFS = [
    { key: "merchantName", label: "Merchant", width: 190, type: "select-merchant" },
    { key: "branchName", label: "Branch", width: 190, type: "select-branch" },
    { key: "driverRef", label: "Driver (name/phone)", width: 190, type: "select-driver" },
    { key: "orderDate", label: "Date", width: 130, type: "date" },
    { key: "orderTime", label: "Time", width: 100, type: "time" },
    { key: "customerName", label: "Customer", width: 150 },
    { key: "customerPhone", label: "Phone", width: 120 },
    { key: "paymentTypeRaw", label: "Payment", width: 160, type: "select-payment" },
    { key: "orderAmount", label: "Amount (KWD)", width: 110, type: "number" },
    { key: "area", label: "Area", width: 160, type: "select-area" },
    { key: "block", label: "Block", width: 130, type: "select-block" },
    { key: "street", label: "Street (optional)", width: 190, type: "select-street" },
    { key: "building", label: "Building (optional)", width: 110 },
    { key: "floor", label: "Floor (optional)", width: 100 },
    { key: "apartment", label: "Apt (optional)", width: 100 },
    { key: "notes", label: "Notes", width: 160 },
    { key: "referenceId", label: "Ref Id", width: 110 },
];

const emptyFields = () =>
    FIELD_DEFS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const compactSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: 34,
        border: "none",
        boxShadow: "none",
        backgroundColor: "transparent",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    indicatorsContainer: (base) => ({ ...base, padding: "0 4px" }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 20, minWidth: 220 }),
};

const filterOption = (option, input) =>
    normalize(option.label).includes(normalize(input));

// Faint hairlines between every cell and every row so the grid reads as
// distinct cells instead of one continuous tinted block. A cool blue-gray
// rather than plain white so it sits naturally on the dark navy theme
// instead of reading as a generic spreadsheet line.
const CELL_DIVIDER = {
    borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
    borderRight: "1px solid rgba(148, 163, 184, 0.07)",
};

// Injected once, scoped by the .wow-import-grid wrapper class, so the
// table gets real hover/focus states without touching any global
// stylesheet.
const GRID_POLISH_CSS = `
.wow-import-grid {
    border-radius: 12px;
    overflow: auto;
    border: 1px solid rgba(148, 163, 184, 0.14);
    box-shadow: 0 10px 30px -14px rgba(0, 0, 0, 0.55);
}
.wow-import-grid table { background: transparent; }
.wow-import-grid thead th {
    background: linear-gradient(180deg, rgba(148, 163, 184, 0.09), rgba(148, 163, 184, 0.03));
    letter-spacing: 0.04em;
}
.wow-import-grid tbody tr {
    transition: background-color 0.12s ease;
}
.wow-import-grid tbody tr:hover {
    background-color: rgba(148, 163, 184, 0.045) !important;
}
.wow-import-grid .cell-chip {
    transition: border-color 0.12s ease, background-color 0.12s ease, box-shadow 0.12s ease;
}
.wow-import-grid input:focus,
.wow-import-grid select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.45) inset;
    border-radius: 6px;
    background-color: rgba(13, 110, 253, 0.06);
}
`;

const ImportManualOrders = () => {
    const [file, setFile] = useState(null);
    const [gridRows, setGridRows] = useState(null); // null = nothing previewed yet
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [merchantOptions, setMerchantOptions] = useState([]);
    const [areaOptions, setAreaOptions] = useState([]);
    const [editModalRow, setEditModalRow] = useState(null);
    const [alertConfig, setAlertConfig] = useContext(alertContext);
    const fileInputRef = useRef(null);
    const token = localStorage.getItem("token");
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const branchCacheRef = useRef({});
    const blockCacheRef = useRef({});
    const streetCacheRef = useRef({});
    const gridRowsRef = useRef(null);
    const autoRecheckTimerRef = useRef(null);
    const [isAutoChecking, setIsAutoChecking] = useState(false);

    useEffect(() => {
        gridRowsRef.current = gridRows;
    }, [gridRows]);

    useEffect(
        () => () => {
            if (autoRecheckTimerRef.current) clearTimeout(autoRecheckTimerRef.current);
        },
        [],
    );

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}merchant-list`, authHeaders)
            .then((res) => {
                if (res.data.code === 1) {
                    setMerchantOptions(
                        res.data.data.map((m) => ({ value: m._id, label: m.merchantName })),
                    );
                }
            })
            .catch(console.error);

        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}location/areas`, authHeaders)
            .then((res) => {
                if (res.data.code === 1) {
                    setAreaOptions(res.data.data.map((a) => ({ value: a.value, label: a.value })));
                }
            })
            .catch(console.error);
        // eslint-disable-next-line
    }, []);

    const loadDriverOptions = useCallback(
        (inputValue, callback) => {
            axios
                .get(
                    `${process.env.REACT_APP_API_ENDPOINT}driver-search?search=${encodeURIComponent(
                        inputValue || "",
                    )}`,
                    authHeaders,
                )
                .then((res) => {
                    if (res.data.code === 1) {
                        callback(
                            res.data.data.map((d) => ({
                                value: d._id,
                                name: d.name,
                                label: `${d.name}${d.workPhone ? ` (${d.workPhone})` : ""}`,
                            })),
                        );
                    } else {
                        callback([]);
                    }
                })
                .catch(() => callback([]));
        },
        // eslint-disable-next-line
        [token],
    );

    const resetAll = () => {
        setFile(null);
        setGridRows(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleFileChange = (e) => {
        setGridRows(null);
        setFile(e.target.files?.[0] ?? null);
    };

    const rowsFromResponseData = (data) =>
        (data?.rows ?? []).map((r) => ({
            rowNumber: r.rowNumber,
            ok: !!r.ok,
            imported: !!r.orderId,
            orderId: r.orderId ?? null,
            fields: { ...emptyFields(), ...(r.fields ?? {}) },
            fieldErrors: r.fieldErrors ?? {},
            fieldNotes: r.fieldNotes ?? (r.summary?.fieldNotes || {}),
            errors: r.errors ?? [],
            matchNotes: r.summary?.matchNotes ?? [],
            summary: r.summary ?? null,
            merchantOpt: null,
            branchOpt: null,
            driverOpt: null,
            areaOpt: null,
            blockOpt: null,
            streetOpt: null,
            branchOptions: [],
            blockOptions: [],
            streetOptions: [],
        }));

    const runPreview = () => {
        if (!file) return;
        setIsPreviewing(true);
        setGridRows(null);

        const formData = new FormData();
        formData.append("file", file);

        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}order/manual-import/preview`,
                formData,
                authHeaders,
            )
            .then((res) => {
                setIsPreviewing(false);
                if (res.data.code === 1) {
                    setGridRows(rowsFromResponseData(res.data.data));
                } else {
                    setAlertConfig({
                        show: true,
                        message: res.data.message || "Preview failed",
                        type: "danger",
                    });
                }
            })
            .catch((err) => {
                setIsPreviewing(false);
                setAlertConfig({
                    show: true,
                    message: err?.response?.data?.message || "Preview failed",
                    type: "danger",
                });
            });
    };

    const patchRow = (rowNumber, patch) => {
        setGridRows((prev) =>
            prev.map((r) => (r.rowNumber === rowNumber ? { ...r, ...patch } : r)),
        );
        scheduleAutoRecheck();
    };

    const updateCell = (rowNumber, key, value) => {
        setGridRows((prev) =>
            prev.map((r) =>
                r.rowNumber === rowNumber
                    ? { ...r, fields: { ...r.fields, [key]: value } }
                    : r,
            ),
        );
        scheduleAutoRecheck();
    };

    const onMerchantChange = async (rowNumber, option) => {
        patchRow(rowNumber, {
            merchantOpt: option,
            branchOpt: null,
            branchOptions: [],
            fields: {
                ...gridRows.find((r) => r.rowNumber === rowNumber).fields,
                merchantName: option?.label ?? "",
                branchName: "",
            },
        });
        if (!option) return;
        let options = branchCacheRef.current[option.value];
        if (!options) {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}branch-list/${option.value}`,
                    authHeaders,
                );
                options =
                    res.data.code === 1 ? res.data.data.map((b) => ({ value: b._id, label: b.name })) : [];
            } catch {
                options = [];
            }
            branchCacheRef.current[option.value] = options;
        }
        patchRow(rowNumber, { branchOptions: options });
    };

    const onBranchChange = (rowNumber, option) => {
        const row = gridRows.find((r) => r.rowNumber === rowNumber);
        patchRow(rowNumber, {
            branchOpt: option,
            fields: { ...row.fields, branchName: option?.label ?? "" },
        });
    };

    const onDriverChange = (rowNumber, option) => {
        const row = gridRows.find((r) => r.rowNumber === rowNumber);
        patchRow(rowNumber, {
            driverOpt: option,
            fields: { ...row.fields, driverRef: option?.name ?? option?.label ?? "" },
        });
    };

    const onAreaChange = async (rowNumber, option) => {
        const row = gridRows.find((r) => r.rowNumber === rowNumber);
        patchRow(rowNumber, {
            areaOpt: option,
            blockOpt: null,
            streetOpt: null,
            blockOptions: [],
            streetOptions: [],
            fields: { ...row.fields, area: option?.label ?? "", block: "", street: "" },
        });
        if (!option) return;
        let options = blockCacheRef.current[option.value];
        if (!options) {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}location/blocks/${encodeURIComponent(option.value)}`,
                    authHeaders,
                );
                options =
                    res.data.code === 1 ? res.data.data.map((b) => ({ value: b.value, label: b.value })) : [];
            } catch {
                options = [];
            }
            blockCacheRef.current[option.value] = options;
        }
        patchRow(rowNumber, { blockOptions: options });
    };

    const onBlockChange = async (rowNumber, option) => {
        const row = gridRows.find((r) => r.rowNumber === rowNumber);
        patchRow(rowNumber, {
            blockOpt: option,
            streetOpt: null,
            streetOptions: [],
            fields: { ...row.fields, block: option?.label ?? "", street: "" },
        });
        if (!option || !row.areaOpt) return;
        const key = `${row.areaOpt.value}|${option.value}`;
        let options = streetCacheRef.current[key];
        if (!options) {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}location/streets/${encodeURIComponent(
                        row.areaOpt.value,
                    )}/${encodeURIComponent(option.value)}`,
                    authHeaders,
                );
                options =
                    res.data.code === 1 ? res.data.data.map((s) => ({ value: s.value, label: s.value })) : [];
            } catch {
                options = [];
            }
            streetCacheRef.current[key] = options;
        }
        patchRow(rowNumber, { streetOptions: options });
    };

    const onStreetChange = (rowNumber, option) => {
        const row = gridRows.find((r) => r.rowNumber === rowNumber);
        patchRow(rowNumber, {
            streetOpt: option,
            fields: { ...row.fields, street: option?.label ?? "" },
        });
    };

    const onPaymentChange = (rowNumber, option) => {
        updateCell(rowNumber, "paymentTypeRaw", option?.value ?? "");
    };

    const removeRow = (rowNumber) => {
        setGridRows((prev) => prev.filter((r) => r.rowNumber !== rowNumber));
    };

    const buildTextRowsPayload = () =>
        gridRows.map((r) => ({ rowNumber: r.rowNumber, ...r.fields }));

    const mergeResponseIntoGrid = (data) => {
        const byRow = {};
        (data?.rows ?? []).forEach((r) => {
            byRow[r.rowNumber] = r;
        });
        setGridRows((prev) =>
            prev.map((r) => {
                const res = byRow[r.rowNumber];
                if (!res) return r;
                return {
                    ...r,
                    ok: !!res.ok,
                    imported: !!res.orderId || r.imported,
                    orderId: res.orderId ?? r.orderId,
                    fields: { ...r.fields, ...(res.fields ?? {}) },
                    fieldErrors: res.fieldErrors ?? {},
                    fieldNotes: res.fieldNotes ?? (res.summary?.fieldNotes || {}),
                    errors: res.errors ?? [],
                    matchNotes: res.summary?.matchNotes ?? [],
                    summary: res.summary ?? r.summary,
                };
            }),
        );
    };

    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

    const downloadTemplate = () => {
        setIsDownloadingTemplate(true);
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}order/manual-import/sample-template`, {
                ...authHeaders,
                responseType: "blob",
            })
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "manual-order-import-template.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                setAlertConfig({
                    show: true,
                    message: "Could not download the template",
                    type: "danger",
                });
            })
            .finally(() => setIsDownloadingTemplate(false));
    };

    // The only way rows get re-validated after the initial preview: every
    // edit (typed, or picked from a dropdown) schedules this, debounced so
    // a burst of keystrokes/picks only fires one request. Silent by design
    // -- there's no button anymore, so it never shows an error popup for a
    // row-level validation failure; those just show up as red/amber/green
    // on the row and cells once the response lands. A real network/server
    // failure still surfaces via the alert, since that's not something the
    // colored cells can communicate.
    const runAutoRecheck = (rows) => {
        if (!rows || rows.length === 0) return;
        setIsAutoChecking(true);

        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}order/manual-import/preview-text-rows`,
                { rows: rows.map((r) => ({ rowNumber: r.rowNumber, ...r.fields })) },
                authHeaders,
            )
            .then((res) => {
                setIsAutoChecking(false);
                if (res.data.code === 1) {
                    mergeResponseIntoGrid(res.data.data);
                } else {
                    setAlertConfig({
                        show: true,
                        message: res.data.message || "Could not re-check the rows",
                        type: "danger",
                    });
                }
            })
            .catch((err) => {
                setIsAutoChecking(false);
                setAlertConfig({
                    show: true,
                    message: err?.response?.data?.message || "Could not re-check the rows",
                    type: "danger",
                });
            });
    };

    // Debounced: fires ~800ms after the last edit to any cell, so typing
    // or picking from a dropdown doesn't fire a request per keystroke,
    // but the grid still settles into fresh red/amber/green state on its
    // own -- no button to click.
    const scheduleAutoRecheck = () => {
        if (autoRecheckTimerRef.current) clearTimeout(autoRecheckTimerRef.current);
        autoRecheckTimerRef.current = setTimeout(() => {
            autoRecheckTimerRef.current = null;
            const rows = gridRowsRef.current;
            if (!rows || rows.length === 0) return;
            runAutoRecheck(rows);
        }, 800);
    };

    const runImport = () => {
        if (!gridRows || gridRows.length === 0) return;
        setIsImporting(true);
        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}order/manual-import/commit-text-rows`,
                { rows: buildTextRowsPayload() },
                authHeaders,
            )
            .then((res) => {
                setIsImporting(false);
                if (res.data.code === 1) {
                    mergeResponseIntoGrid(res.data.data);
                    setAlertConfig({
                        show: true,
                        message: `Imported ${res.data.data.succeeded} of ${res.data.data.total} order(s)`,
                        type: res.data.data.failed > 0 ? "warning" : "success",
                    });
                } else {
                    setAlertConfig({
                        show: true,
                        message: res.data.message || "Import failed",
                        type: "danger",
                    });
                }
            })
            .catch((err) => {
                setIsImporting(false);
                setAlertConfig({
                    show: true,
                    message: err?.response?.data?.message || "Import failed",
                    type: "danger",
                });
            });
    };

    const stats = useMemo(() => {
        if (!gridRows) return null;
        const imported = gridRows.filter((r) => r.imported).length;
        const ready = gridRows.filter((r) => r.ok && !r.imported).length;
        const needsFix = gridRows.filter((r) => !r.ok && !r.imported).length;
        return { total: gridRows.length, imported, ready, needsFix };
    }, [gridRows]);

    // No more whole-row wash. The row itself stays neutral (just a plain
    // hover highlight below) -- status now lives only on the one cell
    // that actually has a problem, via cellWrapStyle, plus the existing
    // Status badge in the first column. This is what "only highlight the
    // error/issue cell, not the full row" means in code.
    const rowStatusStyle = (row) => {
        if (row.imported) return { opacity: 0.75 };
        return {};
    };

    // No outline/border and no background wash on the cell at all anymore
    // -- every field just looks like plain, clean text/input. Whether a
    // field has a problem is now shown entirely by the small corner badge
    // below (IssueBadge), never by coloring or outlining the cell itself.
    const cellWrapStyle = () => ({});

    // The one and only "something's wrong here" signal: a small round
    // badge pinned to the corner of the cell, with the actual message in
    // its tooltip. Red = blocking error, amber = approximate match worth
    // a glance. No badge at all when the field is fine.
    const IssueBadge = ({ hasError, hasNote, title }) => {
        if (!hasError && !hasNote) return null;
        return (
            <span
                title={title}
                className="d-inline-flex align-items-center justify-content-center fw-bold"
                style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 15,
                    height: 15,
                    borderRadius: "50%",
                    fontSize: 10,
                    lineHeight: 1,
                    color: "#1a1a1a",
                    backgroundColor: hasError ? "#ff6b6e" : "#ffc107",
                    border: "2px solid var(--bs-body-bg)",
                    cursor: "help",
                    zIndex: 1,
                }}
            >
                {hasError ? "!" : "?"}
            </span>
        );
    };

    const inputBaseStyle = {
        background: "transparent",
        border: "none",
        outline: "none",
        width: "100%",
        padding: "2px 8px",
        fontSize: "0.8rem",
        lineHeight: 1.4,
        color: "inherit",
    };

    const SELECT_FIELD_RESOLVED = {
        merchantName: (row) => !!row.merchantOpt,
        branchName: (row) => !!row.branchOpt,
        driverRef: (row) => !!row.driverOpt,
        area: (row) => !!row.areaOpt,
        block: (row) => !!row.blockOpt,
        street: (row) => !!row.streetOpt,
    };

    const renderPickerCell = (row, f) => {
        const err = row.fieldErrors?.[f.key];
        const note = !err && row.fieldNotes?.[f.key];
        const disabled = row.imported;
        const wrap = cellWrapStyle(!!err, !!note);
        const title = err || note || "";
        const value = row.fields[f.key];
        const resolved = SELECT_FIELD_RESOLVED[f.key]?.(row);

        return (
            <td key={f.key} className="p-0" style={CELL_DIVIDER} title={title}>
                <div
                    className="cell-chip d-flex align-items-center gap-1 px-2 py-1 m-1 position-relative"
                    style={wrap}
                >
                    <IssueBadge hasError={!!err} hasNote={!!note} title={title} />
                    <span
                        className={`text-truncate fs-8 flex-grow-1 ${value ? (resolved ? "" : "text-muted fst-italic") : "text-muted"
                            }`}
                    >
                        {value || "— empty —"}
                    </span>
                    {!disabled && (
                        <button
                            type="button"
                            className="btn btn-icon btn-sm btn-light-primary flex-shrink-0"
                            style={{ width: 18, height: 18, minHeight: 18, padding: 0 }}
                            title="Pick from your data"
                            onClick={() => setEditModalRow(row.rowNumber)}
                        >
                            <i className="bi bi-pencil-square fs-9" />
                        </button>
                    )}
                </div>
            </td>
        );
    };

    const renderCell = (row, f) => {
        const err = row.fieldErrors?.[f.key];
        const note = !err && row.fieldNotes?.[f.key];
        const disabled = row.imported;
        const wrap = cellWrapStyle(!!err, !!note);
        const title = err || note || "";

        if (
            f.type === "select-merchant" ||
            f.type === "select-branch" ||
            f.type === "select-driver" ||
            f.type === "select-area" ||
            f.type === "select-block" ||
            f.type === "select-street"
        ) {
            return renderPickerCell(row, f);
        }
        if (f.type === "select-payment") {
            return (
                <td key={f.key} className="p-0" style={CELL_DIVIDER} title={title}>
                    <div className="cell-chip m-1 position-relative" style={wrap}>
                        <IssueBadge hasError={!!err} hasNote={!!note} title={title} />
                        <select
                            className="form-select form-select-sm border-0 bg-transparent"
                            style={{ fontSize: "0.8rem", padding: "2px 8px", minHeight: "unset", lineHeight: 1.4 }}
                            value={row.fields.paymentTypeRaw || ""}
                            disabled={disabled}
                            onChange={(e) => updateCell(row.rowNumber, "paymentTypeRaw", e.target.value)}
                        >
                            <option value="">Select type</option>
                            {PAYMENT_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </td>
            );
        }

        return (
            <td key={f.key} className="p-0" style={CELL_DIVIDER} title={title}>
                <div className="cell-chip m-1 position-relative" style={wrap}>
                    <IssueBadge hasError={!!err} hasNote={!!note} title={title} />
                    <input
                        type={
                            f.type === "number" ? "number" : f.type === "date" || f.type === "time" ? f.type : "text"
                        }
                        step={f.type === "number" ? "0.001" : undefined}
                        style={inputBaseStyle}
                        value={row.fields[f.key]}
                        disabled={disabled}
                        onChange={(e) => updateCell(row.rowNumber, f.key, e.target.value)}
                    />
                </div>
            </td>
        );
    };

    return (
        <div className="d-flex flex-column flex-root">
            <Alert />
            <div className="page d-flex flex-row flex-column-fluid">
                <div
                    className="wrapper d-flex flex-column flex-row-fluid"
                    id="kt_wrapper"
                >
                    <Header />
                    <div className="app-body">
                        <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                            <div
                                id="kt_toolbar_container"
                                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
                            >
                                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                                    <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                                        Import Manual Orders
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div
                            id="kt_content_container"
                            className="d-flex flex-column-fluid align-items-start container-xxl"
                        >
                            <div className="content flex-row-fluid" id="kt_content">
                                <PermissionGate required={`import_manual_orders`}>
                                    <div className="card mb-6">
                                        <div className="p-6">
                                            <p className="text-muted mb-4">
                                                Download the template below and fill it in Excel —
                                                Merchant, Branch, Driver, Payment Type, Area, Block
                                                and Street are locked dropdown columns in the file
                                                itself, so there's nothing to mistype there before
                                                you even upload it. Branch narrows to the Merchant
                                                you pick, Block narrows to the Area you pick, and
                                                Street narrows to the Area + Block you pick. In
                                                desktop Excel that narrowing just works, no setup.
                                                In Google Sheets, narrowing needs a one-time script
                                                (included as a separate file — paste it into
                                                Extensions &gt; Apps Script once, authorize it, and
                                                every template narrows correctly from then on); until
                                                you add it, Sheets will still only let you pick from
                                                a list, it just won't narrow yet, and if a row's
                                                Branch/Block/Street still doesn't match its
                                                Merchant/Area, that row gets flagged right after
                                                upload with a clear message, same as any other issue.
                                                Once uploaded, those same fields are editable as live
                                                dropdowns below too — if a row still needs a fix, you
                                                can't type the wrong value by hand. Area and Block
                                                are required; Street is optional — if
                                                you leave it blank, a real street from the matched
                                                Block is auto-assigned (flagged amber so you can
                                                double-check it). Building, Floor and Apartment are
                                                optional too. Every row re-checks itself automatically
                                                a moment after you edit any field — green rows are
                                                ready, red rows still need a fix, amber rows matched
                                                approximately and are worth a glance. Each row becomes
                                                a completed order: the merchant's wallet is debited
                                                for the delivery fee, the driver is credited their
                                                share, and company commission is recorded, same as a
                                                normal order.
                                            </p>

                                            <button
                                                type="button"
                                                className="btn btn-light-primary d-flex align-items-center gap-2 mb-4"
                                                disabled={isDownloadingTemplate}
                                                onClick={downloadTemplate}
                                            >
                                                <i className="bi bi-download" />
                                                {isDownloadingTemplate ? "Downloading..." : "Download blank template"}
                                            </button>

                                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-4">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".xlsx,.xls"
                                                    className="form-control"
                                                    style={{ maxWidth: "420px" }}
                                                    onChange={handleFileChange}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    disabled={!file || isPreviewing}
                                                    onClick={runPreview}
                                                >
                                                    {isPreviewing ? "Checking..." : "Preview"}
                                                </button>
                                                {gridRows && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-light"
                                                        onClick={resetAll}
                                                    >
                                                        Start Over
                                                    </button>
                                                )}
                                            </div>

                                            {stats && (
                                                <>
                                                    <div
                                                        className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 px-4 py-3 rounded-3"
                                                        style={{
                                                            backgroundColor: "rgba(148, 163, 184, 0.045)",
                                                            border: "1px solid rgba(148, 163, 184, 0.12)",
                                                        }}
                                                    >
                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                            <span
                                                                className="d-inline-flex align-items-center gap-1 fw-bold fs-7 px-3 py-1 rounded-pill"
                                                                style={{
                                                                    color: "#2dd683",
                                                                    backgroundColor: "rgba(25, 135, 84, 0.14)",
                                                                    border: "1px solid rgba(25, 135, 84, 0.3)",
                                                                }}
                                                            >
                                                                <i className="bi bi-check-circle-fill fs-8" />
                                                                {stats.ready} ready
                                                            </span>
                                                            {stats.needsFix > 0 && (
                                                                <span
                                                                    className="d-inline-flex align-items-center gap-1 fw-bold fs-7 px-3 py-1 rounded-pill"
                                                                    style={{
                                                                        color: "#ff6b6e",
                                                                        backgroundColor: "rgba(220, 53, 69, 0.14)",
                                                                        border: "1px solid rgba(220, 53, 69, 0.3)",
                                                                    }}
                                                                >
                                                                    <i className="bi bi-exclamation-circle-fill fs-8" />
                                                                    {stats.needsFix} need fixing
                                                                </span>
                                                            )}
                                                            {stats.imported > 0 && (
                                                                <span
                                                                    className="d-inline-flex align-items-center gap-1 fw-bold fs-7 px-3 py-1 rounded-pill"
                                                                    style={{
                                                                        color: "#6ea8fe",
                                                                        backgroundColor: "rgba(13, 110, 253, 0.14)",
                                                                        border: "1px solid rgba(13, 110, 253, 0.3)",
                                                                    }}
                                                                >
                                                                    <i className="bi bi-box-seam-fill fs-8" />
                                                                    {stats.imported} imported
                                                                </span>
                                                            )}
                                                            {isAutoChecking && (
                                                                <span className="text-muted fs-8 d-flex align-items-center gap-1 ms-1">
                                                                    <span
                                                                        className="spinner-border spinner-border-sm"
                                                                        style={{ width: 11, height: 11, borderWidth: 2 }}
                                                                    />
                                                                    checking your changes...
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="d-flex flex-wrap align-items-center gap-3 fs-8 text-muted">
                                                            <span className="d-flex align-items-center gap-1">
                                                                <span
                                                                    className="d-inline-block"
                                                                    style={{
                                                                        width: 9,
                                                                        height: 9,
                                                                        borderRadius: 999,
                                                                        backgroundColor: "rgba(25, 135, 84, 0.8)",
                                                                    }}
                                                                />
                                                                ready
                                                            </span>
                                                            <span className="d-flex align-items-center gap-1">
                                                                <span
                                                                    className="d-inline-block"
                                                                    style={{
                                                                        width: 9,
                                                                        height: 9,
                                                                        borderRadius: 999,
                                                                        backgroundColor: "rgba(220, 53, 69, 0.8)",
                                                                    }}
                                                                />
                                                                needs fixing
                                                            </span>
                                                            <span className="d-flex align-items-center gap-1">
                                                                <span
                                                                    className="d-inline-block"
                                                                    style={{
                                                                        width: 9,
                                                                        height: 9,
                                                                        borderRadius: 999,
                                                                        backgroundColor: "rgba(255, 193, 7, 0.8)",
                                                                    }}
                                                                />
                                                                approximate match
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <style>{GRID_POLISH_CSS}</style>
                                                    <div
                                                        className="wow-import-grid table-responsive mb-4"
                                                        style={{ maxHeight: "68vh" }}
                                                    >
                                                        <table
                                                            className="table table-sm align-middle mb-0"
                                                            style={{ borderCollapse: "separate", borderSpacing: 0 }}
                                                        >
                                                            <thead>
                                                                <tr
                                                                    className="text-muted fw-bold fs-8 text-uppercase"
                                                                    style={{
                                                                        position: "sticky",
                                                                        top: 0,
                                                                        zIndex: 3,
                                                                        backgroundColor: "var(--bs-body-bg)",
                                                                    }}
                                                                >
                                                                    <th
                                                                        className="text-center"
                                                                        style={{
                                                                            width: 40,
                                                                            padding: "10px 8px",
                                                                            ...CELL_DIVIDER,
                                                                            borderBottom: "2px solid rgba(13, 110, 253, 0.28)",
                                                                        }}
                                                                    >
                                                                        #
                                                                    </th>
                                                                    <th
                                                                        style={{
                                                                            width: 110,
                                                                            padding: "10px 12px",
                                                                            ...CELL_DIVIDER,
                                                                            borderBottom: "2px solid rgba(13, 110, 253, 0.28)",
                                                                        }}
                                                                    >
                                                                        Status
                                                                    </th>
                                                                    {FIELD_DEFS.map((f) => (
                                                                        <th
                                                                            key={f.key}
                                                                            style={{
                                                                                minWidth: f.width,
                                                                                padding: "10px 12px",
                                                                                ...CELL_DIVIDER,
                                                                                borderBottom: "2px solid rgba(13, 110, 253, 0.28)",
                                                                            }}
                                                                        >
                                                                            {f.label}
                                                                        </th>
                                                                    ))}
                                                                    <th
                                                                        style={{
                                                                            width: 36,
                                                                            padding: "10px 8px",
                                                                            ...CELL_DIVIDER,
                                                                            borderBottom: "2px solid rgba(13, 110, 253, 0.28)",
                                                                        }}
                                                                    />
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {gridRows.map((row) => (
                                                                    <tr key={row.rowNumber} style={rowStatusStyle(row)}>
                                                                        <td
                                                                            className="text-center text-muted fs-8"
                                                                            style={{ ...CELL_DIVIDER, padding: "8px" }}
                                                                        >
                                                                            {row.rowNumber}
                                                                        </td>
                                                                        <td style={{ ...CELL_DIVIDER, padding: "6px 10px" }}>
                                                                            {row.imported ? (
                                                                                <span
                                                                                    className="d-inline-flex align-items-center gap-1 fw-semibold fs-9 px-2 py-1 rounded-pill"
                                                                                    style={{
                                                                                        color: "#6ea8fe",
                                                                                        backgroundColor: "rgba(13, 110, 253, 0.14)",
                                                                                        border: "1px solid rgba(13, 110, 253, 0.3)",
                                                                                    }}
                                                                                    title={row.orderId ? `Order ${row.orderId}` : ""}
                                                                                >
                                                                                    <i className="bi bi-check-circle-fill" />
                                                                                    Imported
                                                                                </span>
                                                                            ) : row.ok ? (
                                                                                <span
                                                                                    className="d-inline-flex align-items-center gap-1 fw-semibold fs-9 px-2 py-1 rounded-pill"
                                                                                    style={{
                                                                                        color: "#2dd683",
                                                                                        backgroundColor: "rgba(25, 135, 84, 0.14)",
                                                                                        border: "1px solid rgba(25, 135, 84, 0.3)",
                                                                                    }}
                                                                                >
                                                                                    <i className="bi bi-check-circle-fill" />
                                                                                    OK
                                                                                </span>
                                                                            ) : (
                                                                                <span
                                                                                    className="d-inline-flex align-items-center gap-1 fw-semibold fs-9 px-2 py-1 rounded-pill"
                                                                                    style={{
                                                                                        color: "#ff6b6e",
                                                                                        backgroundColor: "rgba(220, 53, 69, 0.14)",
                                                                                        border: "1px solid rgba(220, 53, 69, 0.3)",
                                                                                        cursor: row.errors?.length > 0 ? "help" : "default",
                                                                                    }}
                                                                                    title={row.errors?.length > 0 ? row.errors.join("\n") : ""}
                                                                                >
                                                                                    <i className="bi bi-exclamation-circle-fill" />
                                                                                    Failed
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        {FIELD_DEFS.map((f) => renderCell(row, f))}
                                                                        <td className="text-center" style={CELL_DIVIDER}>
                                                                            {!row.imported && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-icon btn-light-danger"
                                                                                    title="Remove row"
                                                                                    onClick={() => removeRow(row.rowNumber)}
                                                                                >
                                                                                    <i className="bi bi-x-lg fs-8" />
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <div className="d-flex align-items-center justify-content-end gap-3 pt-3 border-top">
                                                        <span className="text-muted fs-7 me-auto">
                                                            {stats.ready} order{stats.ready === 1 ? "" : "s"} ready to import
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-lg"
                                                            disabled={stats.ready === 0 || isImporting}
                                                            onClick={runImport}
                                                        >
                                                            {isImporting
                                                                ? "Importing..."
                                                                : `Confirm Import (${stats.ready} order${stats.ready === 1 ? "" : "s"})`}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </PermissionGate>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
            {editModalRow !== null && (() => {
                const modalRow = gridRows?.find((r) => r.rowNumber === editModalRow);
                if (!modalRow) return null;
                const closeModal = () => setEditModalRow(null);
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.55)",
                            zIndex: 1100,
                        }}
                        onClick={closeModal}
                    >
                        <div
                            className="bg-body rounded shadow-lg p-5"
                            style={{ width: 460, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h4 className="mb-0">Fix row {modalRow.rowNumber}</h4>
                                <button
                                    type="button"
                                    className="btn btn-icon btn-sm btn-light"
                                    onClick={closeModal}
                                >
                                    <i className="bi bi-x-lg fs-7" />
                                </button>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fs-7 fw-bold">Merchant</label>
                                <Select
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    options={merchantOptions}
                                    value={modalRow.merchantOpt}
                                    isClearable
                                    filterOption={filterOption}
                                    placeholder="Select merchant"
                                    onChange={(opt) => onMerchantChange(modalRow.rowNumber, opt)}
                                    styles={{ menu: (b) => ({ ...b, zIndex: 50 }) }}
                                />
                                {modalRow.fields.merchantName && !modalRow.merchantOpt && (
                                    <div className="text-muted fs-8 mt-1">sheet value: {modalRow.fields.merchantName}</div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="form-label fs-7 fw-bold">Branch</label>
                                <Select
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    options={modalRow.branchOptions}
                                    value={modalRow.branchOpt}
                                    isDisabled={!modalRow.merchantOpt}
                                    isClearable
                                    filterOption={filterOption}
                                    placeholder={modalRow.merchantOpt ? "Select branch" : "Pick merchant first"}
                                    onChange={(opt) => onBranchChange(modalRow.rowNumber, opt)}
                                    styles={{ menu: (b) => ({ ...b, zIndex: 50 }) }}
                                />
                                {modalRow.fields.branchName && !modalRow.branchOpt && (
                                    <div className="text-muted fs-8 mt-1">sheet value: {modalRow.fields.branchName}</div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="form-label fs-7 fw-bold">Driver</label>
                                <AsyncSelect
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    cacheOptions
                                    defaultOptions
                                    isClearable
                                    value={modalRow.driverOpt}
                                    loadOptions={loadDriverOptions}
                                    placeholder="Search name or phone"
                                    onChange={(opt) => onDriverChange(modalRow.rowNumber, opt)}
                                    styles={{ menu: (b) => ({ ...b, zIndex: 50 }) }}
                                />
                                {modalRow.fields.driverRef && !modalRow.driverOpt && (
                                    <div className="text-muted fs-8 mt-1">sheet value: {modalRow.fields.driverRef}</div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="form-label fs-7 fw-bold">Area</label>
                                <Select
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    options={areaOptions}
                                    value={modalRow.areaOpt}
                                    isClearable
                                    filterOption={filterOption}
                                    placeholder="Select area"
                                    onChange={(opt) => onAreaChange(modalRow.rowNumber, opt)}
                                    styles={{ menu: (b) => ({ ...b, zIndex: 50 }) }}
                                />
                                {modalRow.fields.area && !modalRow.areaOpt && (
                                    <div className="text-muted fs-8 mt-1">sheet value: {modalRow.fields.area}</div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="form-label fs-7 fw-bold">Block</label>
                                <Select
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    options={modalRow.blockOptions}
                                    value={modalRow.blockOpt}
                                    isDisabled={!modalRow.areaOpt}
                                    isClearable
                                    filterOption={filterOption}
                                    placeholder={modalRow.areaOpt ? "Select block" : "Pick area first"}
                                    onChange={(opt) => onBlockChange(modalRow.rowNumber, opt)}
                                    styles={{ menu: (b) => ({ ...b, zIndex: 50 }) }}
                                />
                                {modalRow.fields.block && !modalRow.blockOpt && (
                                    <div className="text-muted fs-8 mt-1">sheet value: {modalRow.fields.block}</div>
                                )}
                            </div>

                            <div className="mb-5">
                                <label className="form-label fs-7 fw-bold">Street</label>
                                <Select
                                    className="react-select-styled react-select-bordered"
                                    classNamePrefix="react-select"
                                    options={modalRow.streetOptions}
                                    value={modalRow.streetOpt}
                                    isDisabled={!modalRow.blockOpt}
                                    isClearable
                                    filterOption={filterOption}
                                    placeholder={modalRow.blockOpt ? "Select street" : "Pick block first"}
                                    onChange={(opt) => onStreetChange(modalRow.rowNumber, opt)}
                                    styles={{ menu: (b) => ({ ...b, zIndex: 50 }) }}
                                />
                                {modalRow.fields.street && !modalRow.streetOpt && (
                                    <div className="text-muted fs-8 mt-1">sheet value: {modalRow.fields.street}</div>
                                )}
                            </div>

                            <div className="d-flex justify-content-end">
                                <button type="button" className="btn btn-primary" onClick={closeModal}>
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default ImportManualOrders;